import React, { useEffect, useState } from 'react';
import { injectIntl, FormattedMessage, FormattedHTMLMessage } from 'react-intl';
import { connect } from 'dva';
import { Prompt } from 'dva/router';
import _ from 'lodash';
import {
  Form,
  Input,
  Checkbox,
  Select,
  Card,
  Icon,
  Row,
  Button,
  Modal,
  Spin,
  Col,
  Popover,
  Collapse,
  Comment,
  Avatar, InputNumber,
} from 'antd';
import { CancelToken } from 'axios';
import PopoverText from '../PopoverText';
import styles from '../styles.less';
import * as projectService from '../../../services/projects';
import {
  ButtonBar,
  NavModules,
  PageHeader,
  TwoColumnsSelect,
  SelectInput,
  RemoteSelectInput,
} from '../../index';
import { searchCompanyByNameRequest } from '../../../services/companies';
import helpers from '../../../utils/helpers';
import { leadScoringListRequest } from '../../../services/leadScoring';

const { TextArea } = Input;

const analyticTypeToLanguageModel = {
  matomo: 'Matomo',
  google_analytics: 'GoogleAnalytics',
};

const availableOfflineConversationTypes = [
  {
    id: 'all',
    label: <FormattedMessage id="Component.AccountSettings.New.OfflineConversion.Type.All" />,
  },
  {
    id: 'by_scoring_number',
    label: <FormattedMessage id="Component.AccountSettings.New.OfflineConversion.Type.ByScoringNumber" />,
  },
  {
    id: 'by_scoring_profile',
    label: <FormattedMessage id="Component.AccountSettings.New.OfflineConversion.Type.ByScoringProfile" />,
  },
  {
    id: 'by_hubspot_status',
    label: <FormattedMessage id="Component.AccountSettings.New.OfflineConversion.Type.ByHubspotStatus" />,
  },
];

const updateHubspotToken = (projectId, props, setHubspotPortalId) => {
  const tokenValue = props.form.getFieldValue('hubspot_token');
  if (tokenValue === null) {
    return;
  }

  return Modal.confirm({
    title: props.intl.formatMessage({ id: 'Common.Attention' }),
    content: props.intl.formatMessage({ id: 'Component.AccountSettings.New.HubspotSection.ConfirmTokenUpdate' }),
    okText: props.intl.formatMessage({ id: 'Common.Save' }),
    cancelText: props.intl.formatMessage({ id: 'Common.Cancel' }),
    okType: 'danger',
    onOk: () => {
      props.dispatch({
        type: 'projects/updateHubspotToken',
        payload: { projectId, hubspot_token: tokenValue },
      }).then((result) => {
        setHubspotPortalId(result.hubspot_portal_id);
        props.form.resetFields(['hubspot_token']);
        props.dispatch({
          type: 'app/addNotification',
          payload: {
            type: 'success',
            message: 'Common.Success',
            description: 'Component.AccountSettings.Edit.Success.Text',
          },
        }).then(() => props.dispatch({ type: 'app/showNotifications' }));
      });
    },
  });
};

const updateVisitWebhookToken = (projectId, props) => {
  const tokenValue = props.form.getFieldValue('visit_webhook_token');
  if (tokenValue === null) {
    return;
  }

  return Modal.confirm({
    title: props.intl.formatMessage({ id: 'Common.Attention' }),
    content: props.intl.formatMessage({ id: 'Component.AccountSettings.New.Integrations.VisitWebhookToken.ConfirmTokenUpdate' }),
    okText: props.intl.formatMessage({ id: 'Common.Save' }),
    cancelText: props.intl.formatMessage({ id: 'Common.Cancel' }),
    okType: 'danger',
    onOk: () => {
      props.dispatch({
        type: 'projects/updateVisitWebhookToken',
        payload: { projectId, visit_webhook_token: tokenValue },
      }).then(() => {
        props.form.resetFields(['visit_webhook_token']);
        props.dispatch({
          type: 'app/addNotification',
          payload: {
            type: 'success',
            message: 'Common.Success',
            description: 'Component.AccountSettings.Edit.Success.Text',
          },
        }).then(() => props.dispatch({ type: 'app/showNotifications' }));
      });
    },
  });
};

const updateMicrosoftOfflineConversionSettingsSecrets = (projectId, props, field) => {
  const tokenValue = props.form.getFieldValue(`microsoft_offline_conversion_setting_${field}`);
  if (tokenValue === null) {
    return;
  }

  return Modal.confirm({
    title: props.intl.formatMessage({ id: 'Common.Attention' }),
    content: props.intl.formatMessage({ id: 'Component.AccountSettings.New.MicrosoftOfflineConversion.ConfirmSecretUpdate' }),
    okText: props.intl.formatMessage({ id: 'Common.Save' }),
    cancelText: props.intl.formatMessage({ id: 'Common.Cancel' }),
    okType: 'danger',
    onOk: () => {
      props.dispatch({
        type: 'projects/updateMicrosoftOfflineConversionSettingsSecrets',
        payload: { projectId, secret: tokenValue, field },
      }).then(() => {
        props.form.resetFields([`microsoft_offline_conversion_setting_${field}`]);
        props.dispatch({
          type: 'app/addNotification',
          payload: {
            type: 'success',
            message: 'Common.Success',
            description: 'Component.AccountSettings.Edit.Success.Text',
          },
        }).then(() => props.dispatch({ type: 'app/showNotifications' }));
      });
    },
  });
};


const companiesLoader = (projectId) => {
  return (query) => {
    let cancel;

    const requestConfig = {
      cancelToken: new CancelToken((c) => {
        cancel = c;
      }),
    };

    return {
      request: searchCompanyByNameRequest({ projectId, query }, requestConfig),
      cancel,
    };
  };
};

const ProjectItem = (props) => {
  const { id } = props.match.params;
  const projectId = id;

  const [leadScoringProfiles, setLeadScoringProfiles] = useState([]);
  const [isSubmitted, setSubmitted] = useState(false);
  const [dimensionDisabled, setDimensionDisabled] = useState(true);
  const [buttonDisabled, setButtonState] = useState(false);
  const [installCode, setInstallCode] = useState('');
  const [isCopiedInstallCode, setIsCopiedInstallCode] = useState(false);
  const [users, setUsers] = useState(props.projects.projectDetail.users || []);
  const [hubspotPermissions, setHubspotPermissions] = useState(props.projects.projectDetail.hubspot_permissions || []);
  const [hubspotPortalId, setHubspotPortalId] = useState(props.projects.projectDetail.hubspot_portal_id);
  const [dealStages, setDealStages] = useState([]);
  const [createHubspotDeal, setCreateHubspotDeal] = useState(false);
  const [llmPromptResult, setLlmPromptResult] = useState(null);
  const [llmPromptInProgress, setLlmPromptInProgress] = useState(false);
  const [llmPromptTestCompanyId, setLlmPromptTestCompanyId] = useState(null);
  const [microsoftAuthModalOpen, setMicrosoftAuthModalOpen] = useState(false);
  const [microsoftAuthUrl, setMicrosoftAuthUrl] = useState(null);
  const [microsoftAuthUrlLoading, setMicrosoftAuthUrlLoading] = useState(false);
  const [microsoftResponseUri, setMicrosoftResponseUri] = useState('');
  const [microsoftRefreshTokenSaving, setMicrosoftRefreshTokenSaving] = useState(false);
  const pipelines = props.hubspot.pipelines[projectId] || [];
  const llmEnabled = props.app.defaultUiSettings.llm_company_visit_context_completion;
  const analyticType = props.app.defaultUiSettings.analytic_type;

  const modulesList = NavModules();
  const modulesKeys = Object.keys(modulesList);
  const editMode = !!id;

  useEffect(() => {
    props.dispatch({
      type: 'header/update',
      payload: {
        title: 'Component.AccountSettings.List.Title', description: 'Component.AccountSettings.List.Description1',
      },
    });

    props.dispatch({
      type: 'users/loadList',
      payload: {
        filter: {
          client_id: props.app.selectedClient.id,
        },
      },
    });

    leadScoringListRequest({
      projectId,
      limit: 'unlimited',
      page: 1,
      sorting: 'id,asc',
      filter: '',
    }).then(i => setLeadScoringProfiles(i.data));

    if (id) {
      props.dispatch({
        type: 'projects/loadDetail',
        includes: ['users', 'modules', 'hubspotPermissions', 'installCodes', 'projectWebhook', 'projectOfflineConversionSetting'],
        payload: { id },
      }).then((data) => {
        const {
          analytics_site_id, lxp_id_visit_dimension_id, lxp_id_action_dimension_id, target_group_id_visit_dimension_id,
        } = data;
        setCreateHubspotDeal(data.hubspot_create_deals);
        const isDisabled = () => {
          return !(analytics_site_id || lxp_id_visit_dimension_id || lxp_id_action_dimension_id || target_group_id_visit_dimension_id);
        };
        setDimensionDisabled(isDisabled());
        setInstallCode(`${data.lxp_install_code}${data.matomo_install_code}`);
      });
    }
  }, []);

  useEffect(() => {
    setUsers(props.projects.projectDetail.users);
    setHubspotPermissions(props.projects.projectDetail.hubspot_permissions);
  }, [props.projects.projectDetail]);

  useEffect(() => {
    if (props.app.currentProject) {
      handleHubspotPipelineChange(props.app.currentProject.hubspot_pipeline_default_id);
    }
  }, [props.hubspot, props.app.currentProject]);

  useEffect(() => {
    if (!id || !props.projects.projectDetail.hubspot_portal_id) {
      return;
    }

    setHubspotPortalId(props.projects.projectDetail.hubspot_portal_id);

    props.dispatch({
      type: 'hubspot/loadPipelines',
      payload: {
        projectId,
      },
    });

    props.dispatch({
      type: 'hubspotOwners/all',
      payload: {
        projectId,
      },
    });

    props.dispatch({
      type: 'hubspotOwners/assigned',
      payload: {
        projectId,
      },
    });
  }, [props.projects.projectDetail]);


  const backToOverview = () => {
    props.dispatch({
      type: 'app/goToRoute', payload: { routeName: 'ACCOUNTS_LIST' },
    });
  };

  const generateToken = () => {
    Modal.confirm({
      title: props.intl.formatMessage({ id: 'Common.Attention' }),
      content: props.intl.formatMessage({ id: 'Component.AccountSettings.RegenerateTokenModal.Text' }),
      onOk: () => {
        props.dispatch({ type: 'projects/generateToken', payload: { projectId: id } }).then((result) => {
          const modal = Modal.info({
            title: props.intl.formatMessage({ id: 'Component.AccountSettings.TokenModal.Text' }),
            content: <Input.TextArea value={result.auth_token} readOnly />,
            onOk: () => {
              modal.destroy();
            },
          });
        });
      },
      cancelText: props.intl.formatMessage({ id: 'Common.Cancel' }),
    });
  };

  const addUser = () => {
    setButtonState(true);
    setUsers([...(users || []), { id: null, role_id: 1 }]);
  };

  const setUserName = (index, userId) => {
    setButtonState(false);
    setUsers(users.map((item, i) => (index === i ? {
      ...props.users.usersList.find(user => user.id === userId), role_id: 1,
    } : item)));
  };

  const removeUser = (index) => {
    setUsers(users.filter((user, i) => index !== i));
    setButtonState(!users.length);
  };

  const getRemainingUsers = (index) => {
    return props.users.usersList.filter((user) => {
      return !_.without(users, users[index]).some(item => item.id === user.id);
    });
  };

  const handleDimensionFields = (event) => {
    if (event.target.value && /^\d+$/.test(event.target.value)) {
      setDimensionDisabled(false);
    } else {
      setDimensionDisabled(true);
      props.form.resetFields(['target_group_id_visit_dimension_id', 'lxp_id_visit_dimension_id', 'lxp_id_action_dimension_id']);
    }
  };

  const handleHubspotPipelineChange = (v) => {
    const pipeline = pipelines.find(p => p.id === v);
    setDealStages(pipeline ? pipeline.stages : []);
  };

  const getInitialDimensionValue = (fieldName) => {
    return !dimensionDisabled ? props.projects.projectDetail[fieldName] : null;
  };

  const createProject = (payload) => {
    props.dispatch({
      type: 'projects/create', payload,
    }).then((data) => {
      if (data && data.status) {
        const modal = Modal.info({
          title: props.intl.formatMessage({ id: 'Component.AccountSettings.TokenModal.Text' }),
          content: <Input.TextArea value={data.authToken} readOnly />,
          onOk: () => {
            modal.destroy();
            props.dispatch({ type: 'projects/redirectToProjectsList' });
          },
        });
      }
    });
  };

  const testLlmPrompt = async (companyId, prompt) => {
    setLlmPromptInProgress(true);
    const result = await projectService.testLlmPrompt(projectId, companyId, prompt);
    setLlmPromptInProgress(false);
    setLlmPromptResult(result.text);
  };

  const updateProject = async (formData) => {
    const googleOfflineConversionSettings = {};
    const microsoftOfflineConversionSettings = {};

    const data = {};

    Object.keys(formData).forEach((key) => {
      if (key.startsWith('google_offline_conversion_setting_')) {
        const subKey = key.replace('google_offline_conversion_setting_', '');
        googleOfflineConversionSettings[subKey] = formData[key];

        return;
      }

      if (key.startsWith('microsoft_offline_conversion_setting_')) {
        const subKey = key.replace('microsoft_offline_conversion_setting_', '');
        microsoftOfflineConversionSettings[subKey] = formData[key];

        return;
      }

      data[key] = formData[key];
    });

    data.google_offline_conversion_setting = googleOfflineConversionSettings;
    data.microsoft_offline_conversion_setting = microsoftOfflineConversionSettings;

    const success = await props.dispatch({
      type: 'projects/update', payload: data,
    });

    if (!success) {
      // project stored within few requests, so stop in case of any error
      // partial state will be stored
      return;
    }

    if (data.hubspot_users_selected) {
      const owners = await props.dispatch({
        type: 'hubspotOwners/replaceOwners', payload: { projectId, ownersIds: data.hubspot_users_selected },
      });

      if (!owners) {
        // stop update
        return;
      }
    }

    // store hubspot user permissions
    if (data.lxp_hubspot_users_selected) {
      const permissions = await props.dispatch({
        type: 'hubspot/replaceHubspotUserPermissions', payload: { projectId, userIds: data.lxp_hubspot_users_selected },
      });

      if (!permissions) {
        // stop update
        return;
      }
    }

    if (data.hubspot_features && data.hubspot_features.length > 0) {
      const features = await props.dispatch({
        type: 'hubspot/updateSettings',
        payload: {
          projectId,
          hubspot_create_tasks: data.hubspot_features.includes('hubspot_create_tasks'),
          // deal
          hubspot_create_deals: data.hubspot_features.includes('hubspot_create_deals'),
          hubspot_pipeline_default_id: data.hubspot_pipeline_default_id,
          hubspot_pipeline_default_stage_id: data.hubspot_pipeline_default_stage_id,
          // comments
          hubspot_create_comment_on_new_visit: data.hubspot_features.includes('hubspot_create_comment_on_new_visit'),
        },
      });

      if (!features) {
        // stop update
        return;
      }
    }

    props.dispatch({ type: 'app/showNotifications' });
    props.dispatch({ type: 'app/goToRoute', payload: { routeName: 'ACCOUNTS_LIST' } });
  };

  const toNumber = value => (value !== '' ? parseInt(value, 10) : null);

  const formatData = data => (data ? data.filter(Boolean).join(',') : '');

  const handleSubmit = (e) => {
    e.preventDefault();
    props.form.validateFieldsAndScroll(async (err, values) => {
      /*  eslint no-param-reassign: ["error", { "props": true, "ignorePropertyModificationsFor": ["values"] }]*/
      values.allowed_domains = formatData(values.allowed_domains) || null;
      values.disallowed_domains = formatData(values.disallowed_domains) || null;
      values.analytics_site_id = toNumber(values.analytics_site_id);
      values.lxp_id_action_dimension_id = values.lxp_id_action_dimension_id || '';

      if (!err) {
        setSubmitted(true);
        const analyticsId = parseInt(values.analytics_site_id, 10) || null;
        const selectedUsers = values.users ? values.users.filter((item) => {
          return item.id && item.role_id;
        }) : [];

        const payload = {
          ...values, id, users: selectedUsers, analytics_site_id: analyticsId,
        };

        if (id) {
          await updateProject(payload);
        } else {
          await createProject({
            ...payload,
            client_id: props.app.selectedClient.id,
          });
        }
      }
    });
  };

  const cancelAction = () => {
    props.dispatch({
      type: 'app/goToRoute', payload: { routeName: 'ACCOUNTS_LIST' },
    });
  };

  const copyInstallCode = () => {
    if (isCopiedInstallCode) {
      return;
    }
    navigator.clipboard.writeText(installCode).then(() => {
      setIsCopiedInstallCode(true);
      setTimeout(() => {
        setIsCopiedInstallCode(false);
      }, 5000);
    });
  };

  const validateAllowedDomainsValue = (__, values, callback) => {
    /* eslint-disable */
    //const domainRegex = /^((?:(?:(?:\w[\.\-\+]?)*)\w)+)((?:(?:(?:\w[\.\-\+]?){0,62})\w)+)\.(\w{2,6})$/;
    // Allowed *
    const domainRegex = /^(?:\*|(?:\w[\w\-+]*\.)*)(?:\w[\w\-+]*|\*)(?:\.\w{2,6}|\.\*)?$/;

    /* eslint-enable */
    const invalidInputs = values.filter(value => !value.match(domainRegex));
    if (invalidInputs.length === 0) {
      callback();
    } else if (invalidInputs.length === 1) {
      callback(`${invalidInputs.join('')} is not a valid domain'`);
    } else {
      callback(`${invalidInputs.slice(0, -1).join(', ')} and ${invalidInputs.slice(-1)} are not valid domains`);
    }
  };

  const projectGoogleOfflineConversionSetting = props.projects.projectDetail.project_google_offline_conversion_setting || {};
  const projectMicrosoftOfflineConversionSetting = props.projects.projectDetail.project_microsoft_offline_conversion_setting || {};

  return (<Spin
    spinning={!!props.loading.effects['projects/update'] ||
      !!props.loading.effects['projects/create'] ||
      !!props.loading.effects['projects/loadDetail'] ||
      !!props.loading.effects['projects/generateToken'] ||
      !!props.loading.effects['projects/updateHubspotToken'] ||
      !!props.loading.effects['hubspotOwners/loadOwners'] ||
      !!props.loading.effects['hubspotOwners/loadAssignedOwners'] ||
      !!props.loading.effects['hubspotOwners/replaceOwners'] ||
      !!props.loading.effects['hubspot/replaceHubspotUserPermissions'] ||
      !!props.loading.effects['hubspot/updateSettings']
    }
  >

    <Prompt
      when={(props.form.isFieldsTouched() && !isSubmitted)}
      message={props.intl.locale}
    />
    <div className={[styles.containerLarge, styles.containerProject].join(' ')}>
      <div className={[styles.componentHeader]}>
        <PageHeader onBackToOverview={backToOverview}>
          <FormattedMessage id={`Component.AccountSettings.${id ? 'Edit.Title' : 'New.Title1'}`} />
        </PageHeader>
      </div>
      <Form onSubmit={handleSubmit}>
        <Collapse
          bordered={false}
          defaultActiveKey={['0']}
          expandIconPosition="right"
        >
          <Collapse.Panel
            key="account"
            header={<h3><FormattedMessage id="Component.AccountSettings.New.AccountSection.Account" /></h3>}
          >
            <Card className={[styles.noMarginTop, styles.noMarginBottom, styles.noBorderTop].join(' ')}>
              <Row type={'flex'} gutter={32}>
                <Col span={12}>
                  <Form.Item label={<FormattedMessage id="Component.AccountSettings.New.Project.Name" />}>
                    {props.form.getFieldDecorator('name', {
                      initialValue: props.projects.projectDetail.name || '',
                      rules: [{
                        required: true, message: props.intl.formatMessage({ id: 'Common.Field.Required' }),
                      }],
                    })(<Input />)}
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label={<FormattedMessage id="Component.AccountSettings.New.ModulesSection.Title" />}>
                    {props.form.getFieldDecorator('modules', {
                      initialValue: props.projects.projectDetail.modules || [],
                      rules: [{
                        required: true, message: props.intl.formatMessage({ id: 'Common.Field.Required' }),
                      }],
                    })(<Checkbox.Group>
                      {modulesKeys.map(item => (<Checkbox
                        className={styles.Checkbox}
                        value={modulesList[item].code}
                        key={item}
                      >
                        {modulesList[item].label}
                      </Checkbox>))}
                    </Checkbox.Group>)}
                  </Form.Item>
                </Col>
              </Row>
              <Row type={'flex'} gutter={32}>
                <Col span={12}>
                  <Form.Item label={<FormattedMessage id="Component.AccountSettings.New.DomainSection.AllowedDomains" />}>
                    {props.form.getFieldDecorator('allowed_domains', {
                      rules: [{
                        required: false,
                        validator: validateAllowedDomainsValue,
                      }],
                      initialValue: !id ? [] : (props.projects.projectDetail.allowed_domains ? props.projects.projectDetail.allowed_domains.split(',') : []),
                    })(
                      <Select mode="tags" />,
                    )}
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label={<FormattedMessage id="Component.AccountSettings.New.DomainSection.DisallowedDomains" />}>
                    {props.form.getFieldDecorator('disallowed_domains', {
                      rules: [{
                        required: false,
                        validator: validateAllowedDomainsValue,
                      }],
                      initialValue: !id ? [] : (props.projects.projectDetail.disallowed_domains ? props.projects.projectDetail.disallowed_domains.split(',') : []),
                    })(
                      <Select mode="tags" />,
                    )}
                  </Form.Item>
                </Col>
              </Row>
              {id && (<Row>
                <Col>
                  <Button className={styles.topMiddleMargin} type="danger" onClick={generateToken}>
                    <FormattedMessage id="Component.AccountSettings.RegenerateToken" />
                  </Button>
                </Col>
              </Row>)}
            </Card>
          </Collapse.Panel>
          <Collapse.Panel
            key="users"
            header={<h3><FormattedMessage id="Component.AccountSettings.New.UserSection.User" /></h3>}
          >
            <Card className={[styles.noMarginTop, styles.noMarginBottom, styles.noBorderTop].join(' ')}>
              <Row type="flex" gutter={32}>
                {(users || []).map((user, index) => {
                  return (<Col key={index} md={24} lg={12}>
                    <div className={styles.userRoleCard}>
                      <Row type="flex" gutter={32}>
                        <Col span={17} className={styles.userRoleColumn}>
                          <Form.Item>
                            <h4><FormattedHTMLMessage
                              id="Component.AccountSettings.New.UserSection.User"
                            /> {index + 1}</h4>
                            {props.form.getFieldDecorator(`users[${index}].id`, {
                              rules: [{
                                type: 'number', message: props.intl.formatMessage({ id: 'Common.Field.Required' }),
                              }],
                              initialValue: user.id || '',
                            })(<Select onSelect={setUserName.bind(this, index)}>
                              {(getRemainingUsers(index) || []).map(item => (<Select.Option
                                className={styles.Checkbox}
                                value={item.id}
                                key={`${item.id} ${index}`}
                              >
                                {item.first_name} {item.last_name}
                              </Select.Option>))}
                            </Select>)}
                          </Form.Item>
                          <Form.Item>
                            {props.form.getFieldDecorator(`users[${index}].role_id`, {
                              rules: [{
                                type: 'number', message: props.intl.formatMessage({ id: 'Common.Field.Required' }),
                              }],
                              initialValue: user.role_id || '',
                            })(<Select>
                              {(props.app.roles || []).map(item => (
                                <Select.Option className={styles.Checkbox} value={item.id} key={item.id}>
                                  {item.label}
                                </Select.Option>))}
                            </Select>)}
                          </Form.Item>
                        </Col>
                        <Col className={styles.userRoleColumnDelete}>
                          <div className={styles.btnNoBorder}>
                            <Icon type="close" onClick={() => removeUser(index)} />
                          </div>
                          <Popover
                            content={<PopoverText />}
                            trigger="click"
                            title={props.intl.formatMessage({ id: 'Component.AccountSettings.New.AvailableRoles.Title' })}
                          >
                            <a href="##" className={styles.btnNoBorder}>
                              <Icon type="question-circle-o" />
                            </a>
                          </Popover>
                        </Col>
                      </Row>
                    </div>
                  </Col>);
                })}
              </Row>
              <Row type="flex" gutter={32}>
                <Col span={7}>
                  {(users ? users.length : 0) < props.users.usersList.length && (<Button
                    size="large"
                    type="primary"
                    disabled={buttonDisabled}
                    onClick={() => addUser()}
                    className={styles.btnWithBlueBorders}
                  >
                    <Icon type="plus" />
                    <FormattedMessage id="Component.AccountSettings.New.UserSection.AddUser" />
                  </Button>)}
                </Col>
              </Row>
            </Card>
          </Collapse.Panel>
          <Collapse.Panel
            key={analyticType}
            header={<h3><FormattedMessage id={`Component.AccountSettings.New.AnalyticsSection.${analyticTypeToLanguageModel[analyticType]}`} />
            </h3>}
          >
            <Card className={[styles.noMarginTop, styles.noMarginBottom, styles.noBorderTop].join(' ')}>
              <Row type={'flex'} gutter={32}>
                <Col md={12} lg={6}>
                  <Form.Item
                    label={<FormattedMessage id="Component.AccountSettings.New.DomainSection.MatomoIdName" />}
                  >
                    {props.form.getFieldDecorator('analytics_site_id', {
                      initialValue: (props.projects.projectDetail.analytics_site_id || '').toString(),
                      rules: [{
                        required: false,
                        message: props.intl.formatMessage({ id: 'Common.Field.RequiredAndNumber' }),
                      }],
                      onChange: handleDimensionFields,
                    })(<Input />)}
                  </Form.Item>
                </Col>
                <Col md={12} lg={6}>
                  <Form.Item label="Visit Dimension">
                    {props.form.getFieldDecorator('lxp_id_visit_dimension_id', {
                      initialValue: getInitialDimensionValue('lxp_id_visit_dimension_id'),
                      rules: [{
                        required: !dimensionDisabled,
                        message: props.intl.formatMessage({ id: 'Common.Field.RequiredAndNumber' }),
                      }],
                    })(<Input disabled={dimensionDisabled} />)}
                  </Form.Item>
                </Col>
                {analyticType === 'matomo' &&
                <Col md={12} lg={6}>
                  <Form.Item label="Action Dimension">
                    {props.form.getFieldDecorator('lxp_id_action_dimension_id', {
                      initialValue: getInitialDimensionValue('lxp_id_action_dimension_id'),
                      rules: [{
                        required: !dimensionDisabled,
                        message: props.intl.formatMessage({ id: 'Common.Field.RequiredAndNumber' }),
                      }],
                    })(<Input disabled={dimensionDisabled} />)}
                  </Form.Item>
                </Col>
                }
                <Col md={12} lg={6}>
                  <Form.Item label="Target Group Dimension">
                    {props.form.getFieldDecorator('target_group_id_visit_dimension_id', {
                      initialValue: getInitialDimensionValue('target_group_id_visit_dimension_id'),
                      rules: [{
                        required: !dimensionDisabled,
                        message: props.intl.formatMessage({ id: 'Common.Field.RequiredAndNumber' }),
                      }],
                    })(<Input disabled={dimensionDisabled} />)}
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Collapse.Panel>

          {editMode && <Collapse.Panel
            key="integrations" header={<h3><FormattedMessage
              id="Component.AccountSettings.New.Integrations"
            /></h3>}
          >
            <Card className={[styles.noMarginTop, styles.noMarginBottom, styles.noBorderTop].join(' ')}>
              <Row type={'flex'} gutter={32}>
                <Col span={16}>
                  <Form.Item label={<FormattedMessage id="Component.AccountSettings.New.Integrations.VisitWebhookUrl" />}>
                    {props.form.getFieldDecorator('visit_webhook_url', {
                      initialValue: props.projects.projectDetail.project_webhook ? props.projects.projectDetail.project_webhook.webhook_url || '' : '',
                      rules: [{}],
                    })(<Input />)}
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label={<FormattedMessage id="Component.AccountSettings.New.Integrations.VisitWebhookMinimalScore" />}>
                    {props.form.getFieldDecorator('visit_webhook_minimal_score', {
                      initialValue: props.projects.projectDetail.project_webhook ? props.projects.projectDetail.project_webhook.minimal_score || '' : '',
                    })(
                      <InputNumber
                        maxLength={255}
                        min={0}
                        max={10}
                        style={{ fontFamily: 'normal' }}
                      />,
                    )}
                  </Form.Item>
                </Col>
              </Row>
              <Row type={'flex'} gutter={32}>
                <Col span={16}>
                  <Form.Item label={<FormattedMessage id="Component.AccountSettings.New.Integrations.VisitWebhookToken" />}>
                    {props.form.getFieldDecorator('visit_webhook_token', {
                      initialValue: null,
                      rules: [{
                        required: false,
                      }],
                    })(<Input
                      placeholder={!!props.projects.projectDetail.project_webhook && !!props.projects.projectDetail.project_webhook.webhook_token_set ?
                        props.intl.formatMessage({ id: 'Component.AccountSettings.New.Integrations.VisitWebhookTokenSet' })
                        :
                        props.intl.formatMessage({ id: 'Component.AccountSettings.New.Integrations.VisitWebhookTokenNotSet' })}
                    />)}
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Button
                    disabled={!props.form.getFieldValue('visit_webhook_token')}
                    type="primary"
                    style={{ marginTop: '48px' }}
                    onClick={() => updateVisitWebhookToken(projectId, props)}
                  >
                    <FormattedMessage id="Common.Save" />
                  </Button>
                </Col>
              </Row>
            </Card>
          </Collapse.Panel>}

          {editMode && <Collapse.Panel
            key="googleOfflineConversion" header={<h3><FormattedMessage
              id="Component.AccountSettings.New.GoogleOfflineConversion"
            /></h3>}
          >
            <Card className={[styles.noMarginTop, styles.noMarginBottom, styles.noBorderTop].join(' ')}>
              <Row type={'flex'} gutter={32}>
                <Form.Item>
                  {props.form.getFieldDecorator('google_offline_conversion_setting_enabled', {
                    valuePropName: 'checked',
                    initialValue: projectGoogleOfflineConversionSetting.enabled || false,
                  })(
                    <Checkbox>
                      <FormattedMessage id="Component.AccountSettings.New.GoogleOfflineConversion.Enabled" />
                    </Checkbox>,
                  )}
                </Form.Item>
              </Row>
              {props.form.getFieldValue('google_offline_conversion_setting_enabled') && (
                <React.Fragment>
                  <Row type={'flex'} gutter={32}>
                    <Col span={8}>
                      <Form.Item
                        label={
                          <FormattedMessage id="Component.AccountSettings.New.GoogleOfflineConversion.Type" />
                        }
                      >
                        {props.form.getFieldDecorator('google_offline_conversion_setting_type', {
                          initialValue: projectGoogleOfflineConversionSetting.type || null,
                          rules: [
                            {
                              required: true,
                            },
                          ],
                        })(
                          <Select
                            showArrow
                          >
                            {availableOfflineConversationTypes.map(item => (
                              <Select.Option key={item.id} value={item.id}>
                                {item.label}
                              </Select.Option>
                            ))}
                          </Select>,
                        )}
                      </Form.Item>
                    </Col>
                    {props.form.getFieldValue('google_offline_conversion_setting_type') === 'by_scoring_profile' && (
                      <Col span={16}>
                        <Form.Item
                          label={
                            <FormattedMessage id="Component.AccountSettings.New.GoogleOfflineConversion.LeadScoringProfile" />
                          }
                        >
                          {props.form.getFieldDecorator('google_offline_conversion_setting_lead_scoring_profiles', {
                            initialValue: (projectGoogleOfflineConversionSetting.lead_scoring_profiles || []).map(profile => profile.id),
                            rules: [
                              {
                                required: true,
                              },
                            ],
                          })(
                            <Select
                              mode="multiple"
                              showArrow
                              filterOption={helpers.multipleSelectFilter}
                            >
                              {leadScoringProfiles.map(item => (
                                <Select.Option key={item.id} value={item.id}>
                                  {item.name}
                                </Select.Option>
                              ))}
                            </Select>,
                          )}
                        </Form.Item>
                      </Col>
                    )}
                    {props.form.getFieldValue('google_offline_conversion_setting_type') === 'by_scoring_number' && (
                      <Col span={8}>
                        <Form.Item label={<FormattedMessage id="Component.AccountSettings.New.GoogleOfflineConversion.MinimalScore" />}>
                          {props.form.getFieldDecorator('google_offline_conversion_setting_minimal_score', {
                            initialValue: projectGoogleOfflineConversionSetting.minimal_score || '',
                            rules: [
                              {
                                required: true,
                              },
                            ],
                          })(
                            <InputNumber
                              maxLength={255}
                              min={0}
                              max={10}
                              style={{ fontFamily: 'normal' }}
                            />,
                          )}
                        </Form.Item>
                      </Col>
                    )}
                  </Row>
                  <Row type={'flex'} gutter={32}>
                    <Col span={8}>
                      <Form.Item label={<FormattedMessage id="Component.AccountSettings.New.GoogleOfflineConversion.SheetId" />}>
                        {props.form.getFieldDecorator('google_offline_conversion_setting_sheet_id', {
                          initialValue: projectGoogleOfflineConversionSetting.sheet_id || '',
                          rules: [{
                            required: true,
                          }],
                        })(<Input />)}
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item label={<FormattedMessage id="Component.AccountSettings.New.GoogleOfflineConversion.SheetName" />}>
                        {props.form.getFieldDecorator('google_offline_conversion_setting_sheet_name', {
                          initialValue: projectGoogleOfflineConversionSetting.sheet_name || '',
                          rules: [{
                            required: true,
                          }],
                        })(<Input />)}
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item label={<FormattedMessage id="Component.AccountSettings.New.GoogleOfflineConversion.ConversionName" />}>
                        {props.form.getFieldDecorator('google_offline_conversion_setting_conversion_name', {
                          initialValue: projectGoogleOfflineConversionSetting.conversion_name || '',
                          rules: [{
                            required: true,
                          }],
                        })(<Input />)}
                      </Form.Item>
                    </Col>
                  </Row>
                </React.Fragment>)}
            </Card>
          </Collapse.Panel>}

          {editMode && <Collapse.Panel
            key="microsoftOfflineConversion" header={<h3><FormattedMessage
              id="Component.AccountSettings.New.MicrosoftOfflineConversion"
            /></h3>}
          >
            <Card className={[styles.noMarginTop, styles.noMarginBottom, styles.noBorderTop].join(' ')}>
              <Row type={'flex'} gutter={32}>
                <Form.Item>
                  {props.form.getFieldDecorator('microsoft_offline_conversion_setting_enabled', {
                    valuePropName: 'checked',
                    initialValue: projectMicrosoftOfflineConversionSetting.enabled || false,
                  })(
                    <Checkbox>
                      <FormattedMessage id="Component.AccountSettings.New.MicrosoftOfflineConversion.Enabled" />
                    </Checkbox>,
                  )}
                </Form.Item>
              </Row>
              {props.form.getFieldValue('microsoft_offline_conversion_setting_enabled') && (
                <React.Fragment>
                  <Row type={'flex'} gutter={32}>
                    <Col span={8}>
                      <Form.Item
                        label={
                          <FormattedMessage id="Component.AccountSettings.New.MicrosoftOfflineConversion.Type" />
                        }
                      >
                        {props.form.getFieldDecorator('microsoft_offline_conversion_setting_type', {
                          initialValue: projectMicrosoftOfflineConversionSetting.type || null,
                          rules: [
                            {
                              required: true,
                            },
                          ],
                        })(
                          <Select
                            showArrow
                          >
                            {availableOfflineConversationTypes.map(item => (
                              <Select.Option key={item.id} value={item.id}>
                                {item.label}
                              </Select.Option>
                            ))}
                          </Select>,
                        )}
                      </Form.Item>
                    </Col>
                    {props.form.getFieldValue('microsoft_offline_conversion_setting_type') === 'by_scoring_profile' && (
                      <Col span={16}>
                        <Form.Item
                          label={
                            <FormattedMessage id="Component.AccountSettings.New.MicrosoftOfflineConversion.LeadScoringProfile" />
                          }
                        >
                          {props.form.getFieldDecorator('microsoft_offline_conversion_setting_lead_scoring_profiles', {
                            initialValue: (projectMicrosoftOfflineConversionSetting.lead_scoring_profiles || []).map(profile => profile.id),
                            rules: [
                              {
                                required: true,
                              },
                            ],
                          })(
                            <Select
                              mode="multiple"
                              showArrow
                              filterOption={helpers.multipleSelectFilter}
                            >
                              {leadScoringProfiles.map(item => (
                                <Select.Option key={item.id} value={item.id}>
                                  {item.name}
                                </Select.Option>
                              ))}
                            </Select>,
                          )}
                        </Form.Item>
                      </Col>
                    )}
                    {props.form.getFieldValue('microsoft_offline_conversion_setting_type') === 'by_scoring_number' && (
                      <Col span={8}>
                        <Form.Item label={<FormattedMessage id="Component.AccountSettings.New.MicrosoftOfflineConversion.MinimalScore" />}>
                          {props.form.getFieldDecorator('microsoft_offline_conversion_setting_minimal_score', {
                            initialValue: projectMicrosoftOfflineConversionSetting.minimal_score || '',
                            rules: [
                              {
                                required: true,
                              },
                            ],
                          })(
                            <InputNumber
                              maxLength={255}
                              min={0}
                              max={10}
                              style={{ fontFamily: 'normal' }}
                            />,
                          )}
                        </Form.Item>
                      </Col>
                    )}
                  </Row>
                  <Row type={'flex'} gutter={32}>
                    <Col span={12}>
                      <Form.Item label={<FormattedMessage id="Component.AccountSettings.New.MicrosoftOfflineConversion.ClientId" />}>
                        {props.form.getFieldDecorator('microsoft_offline_conversion_setting_client_id', {
                          initialValue: projectMicrosoftOfflineConversionSetting.client_id || '',
                          rules: [{
                            required: true,
                          }],
                        })(<Input />)}
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label={<FormattedMessage id="Component.AccountSettings.New.MicrosoftOfflineConversion.CustomerId" />}>
                        {props.form.getFieldDecorator('microsoft_offline_conversion_setting_customer_id', {
                          initialValue: projectMicrosoftOfflineConversionSetting.customer_id || '',
                          rules: [{
                            required: true,
                          }],
                        })(<Input />)}
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row type={'flex'} gutter={32}>
                    <Col span={12}>
                      <Form.Item label={<FormattedMessage id="Component.AccountSettings.New.MicrosoftOfflineConversion.AccountId" />}>
                        {props.form.getFieldDecorator('microsoft_offline_conversion_setting_account_id', {
                          initialValue: projectMicrosoftOfflineConversionSetting.account_id || '',
                          rules: [{
                            required: true,
                          }],
                        })(<Input />)}
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label={<FormattedMessage id="Component.AccountSettings.New.MicrosoftOfflineConversion.ConversionName" />}>
                        {props.form.getFieldDecorator('microsoft_offline_conversion_setting_conversion_name', {
                          initialValue: projectMicrosoftOfflineConversionSetting.conversion_name || '',
                          rules: [{
                            required: true,
                          }],
                        })(<Input />)}
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row type={'flex'} gutter={32}>
                    <Col span={16}>
                      <Form.Item label={<FormattedMessage id="Component.AccountSettings.New.MicrosoftOfflineConversion.DeveloperToken" />}>
                        {props.form.getFieldDecorator('microsoft_offline_conversion_setting_developer_token', {
                          initialValue: null,
                          rules: [{
                            required: false,
                          }],
                        })(<Input
                          placeholder={!!projectMicrosoftOfflineConversionSetting && !!projectMicrosoftOfflineConversionSetting.developer_token_set ?
                            props.intl.formatMessage({ id: 'Component.AccountSettings.New.MicrosoftOfflineConversion.DeveloperTokenSet' })
                            :
                            props.intl.formatMessage({ id: 'Component.AccountSettings.New.MicrosoftOfflineConversion.DeveloperTokenNotSet' })}
                        />)}
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Button
                        disabled={!props.form.getFieldValue('microsoft_offline_conversion_setting_developer_token')}
                        type="primary"
                        style={{ marginTop: '48px' }}
                        onClick={() => updateMicrosoftOfflineConversionSettingsSecrets(projectId, props, 'developer_token')}
                      >
                        <FormattedMessage id="Common.Save" />
                      </Button>
                    </Col>
                  </Row>
                  <Row type={'flex'} gutter={32}>
                    <Col span={24}>
                      <Form.Item label={<FormattedMessage id="Component.AccountSettings.New.MicrosoftOfflineConversion.RefreshToken" />}>
                        <Button
                          type="primary"
                          disabled={!projectMicrosoftOfflineConversionSetting.developer_token_set || !props.form.getFieldValue('microsoft_offline_conversion_setting_client_id')}
                          onClick={() => {
                            const clientId = props.form.getFieldValue('microsoft_offline_conversion_setting_client_id');
                            setMicrosoftAuthModalOpen(true);
                            setMicrosoftAuthUrl(null);
                            setMicrosoftResponseUri('');
                            setMicrosoftAuthUrlLoading(true);
                            props.dispatch({
                              type: 'projects/getMicrosoftAuthorizationUrl',
                              payload: { projectId, clientId },
                            }).then((result) => {
                              setMicrosoftAuthUrl(result && result.url);
                              setMicrosoftAuthUrlLoading(false);
                            }).catch(() => setMicrosoftAuthUrlLoading(false));
                          }}
                        >
                          <FormattedMessage id="Component.AccountSettings.New.MicrosoftOfflineConversion.RefreshTokenNotSet" />
                        </Button>
                        {!!projectMicrosoftOfflineConversionSetting.refresh_token_set && (
                          <span style={{ marginLeft: 8, color: '#52c41a' }}>
                            <Icon type="check-circle" style={{ marginRight: 4 }} />
                            <FormattedMessage id="Component.AccountSettings.New.MicrosoftOfflineConversion.RefreshTokenSet" />
                          </span>
                        )}
                      </Form.Item>
                    </Col>
                  </Row>
                  <Modal
                    title={<FormattedMessage id="Component.AccountSettings.New.MicrosoftOfflineConversion.RefreshToken.Modal.Title" />}
                    visible={microsoftAuthModalOpen}
                    onCancel={() => setMicrosoftAuthModalOpen(false)}
                    footer={[
                      <Button key="cancel" onClick={() => setMicrosoftAuthModalOpen(false)}>
                        <FormattedMessage id="Common.Cancel" />
                      </Button>,
                      <Button
                        key="save"
                        type="primary"
                        loading={microsoftRefreshTokenSaving}
                        disabled={!microsoftResponseUri.trim()}
                        onClick={() => {
                          setMicrosoftRefreshTokenSaving(true);
                          props.dispatch({
                            type: 'projects/storeMicrosoftRefreshToken',
                            payload: {
                              projectId,
                              responseUri: microsoftResponseUri.trim(),
                              clientId: props.form.getFieldValue('microsoft_offline_conversion_setting_client_id'),
                            },
                          }).then(() => {
                            setMicrosoftRefreshTokenSaving(false);
                            setMicrosoftAuthModalOpen(false);
                            setMicrosoftResponseUri('');
                            props.dispatch({
                              type: 'app/addNotification',
                              payload: {
                                type: 'success',
                                message: 'Common.Success',
                                description: 'Component.AccountSettings.Edit.Success.Text',
                              },
                            }).then(() => props.dispatch({ type: 'app/showNotifications' }));
                          }).catch(() => setMicrosoftRefreshTokenSaving(false));
                        }}
                      >
                        <FormattedMessage id="Common.Save" />
                      </Button>,
                    ]}
                  >
                    <Spin spinning={microsoftAuthUrlLoading}>
                      <p><FormattedMessage id="Component.AccountSettings.New.MicrosoftOfflineConversion.RefreshToken.Modal.Step1" /></p>
                      {microsoftAuthUrl && (
                        <p>
                          <a href={microsoftAuthUrl} target="_blank" rel="noopener noreferrer">
                            {microsoftAuthUrl}
                          </a>
                        </p>
                      )}
                      <p style={{ marginTop: 16 }}>
                        <FormattedMessage id="Component.AccountSettings.New.MicrosoftOfflineConversion.RefreshToken.Modal.Step2" />
                      </p>
                      <Input.TextArea
                        value={microsoftResponseUri}
                        onChange={e => setMicrosoftResponseUri(e.target.value)}
                        placeholder={props.intl.formatMessage({ id: 'Component.AccountSettings.New.MicrosoftOfflineConversion.RefreshToken.Modal.Placeholder' })}
                        autoSize={{ minRows: 3 }}
                      />
                    </Spin>
                  </Modal>

                </React.Fragment>)}
            </Card>
          </Collapse.Panel>}

          {editMode && <Collapse.Panel
            key="hubspot" header={<h3><FormattedMessage
              id="Component.AccountSettings.New.HubspotSection.HubspotTitle"
            /></h3>}
          >
            <Card className={[styles.noMarginTop, styles.noMarginBottom, styles.noBorderTop].join(' ')}>
              <Row type={'flex'} gutter={32}>
                <Col md={12} lg={10}>
                  <Form.Item label="Hubspot Private App API key">
                    {props.form.getFieldDecorator('hubspot_token', {
                      initialValue: null,
                      rules: [{
                        required: false,
                      }],
                    })(<Input
                      placeholder={hubspotPortalId ?
                        props.intl.formatMessage(
                          { id: 'Component.AccountSettings.New.HubspotSection.AccountIntegratedPlaceholder' },
                          { portalId: hubspotPortalId },
                        ) :
                        props.intl.formatMessage({ id: 'Component.AccountSettings.New.HubspotSection.AccountNotIntegratedPlaceholder' })}
                    />)}
                  </Form.Item>
                </Col>
                <Col md={12} lg={2}>
                  <Button
                    disabled={!props.form.getFieldValue('hubspot_token')}
                    type="primary"
                    style={{ marginTop: '48px' }}
                    onClick={() => updateHubspotToken(projectId, props, setHubspotPortalId)}
                  >
                    <FormattedMessage id="Common.Save" />
                  </Button>
                </Col>
              </Row>
              <Row type={'flex'} gutter={32}>
                <Col md={12} lg={10}>
                  <Form.Item
                    label={<FormattedMessage id="Component.AccountSettings.New.HubspotSection.FeaturesLabel" />}
                  >
                    {props.form.getFieldDecorator('hubspot_features', {
                      initialValue: [
                        'hubspot_create_deals',
                        'hubspot_create_tasks',
                        'hubspot_create_comment_on_new_visit',
                      ].filter(v => props.projects.projectDetail[v] === true),
                      rules: [{
                        required: false,
                      }],
                    })(<Checkbox.Group
                      disabled={!hubspotPortalId}
                      options={[
                        {
                          label: <FormattedMessage
                            id="Component.AccountSettings.New.HubspotSection.CreateTaskFeatureLabel"
                          />,
                          value: 'hubspot_create_tasks',
                        },
                        {
                          label: <FormattedMessage
                            id="Component.AccountSettings.New.HubspotSection.CreateDealFeatureLabel"
                          />,
                          value: 'hubspot_create_deals',
                          onChange: (e) => {
                            setCreateHubspotDeal(e.target.checked);
                            if (e.target.checked === false) {
                              props.form.setFieldsValue({
                                hubspot_pipeline_default_id: null,
                                hubspot_pipeline_default_stage_id: null,
                              });
                            }
                          },
                        },
                        {
                          label: <FormattedMessage
                            id="Component.AccountSettings.New.HubspotSection.CreateCommentOnNewVisit"
                          />,
                          value: 'hubspot_create_comment_on_new_visit',
                        },
                      ]}
                    />)}
                  </Form.Item>
                  <Form.Item
                    label={<FormattedMessage id="Component.AccountSettings.New.HubspotSection.DefaultPipelineLabel" />}
                  >
                    {props.form.getFieldDecorator('hubspot_pipeline_default_id', {
                      initialValue: props.projects.projectDetail.hubspot_pipeline_default_id,
                    })(
                      <SelectInput
                        disabled={!createHubspotDeal}
                        showArrow
                        placeholder={<FormattedMessage
                          id="Component.AccountSettings.New.HubspotSection.DefaultPipelinePlaceholder"
                        />}
                        options={pipelines}
                        onChange={(v) => {
                          handleHubspotPipelineChange(v);
                          props.form.setFieldsValue({
                            hubspot_pipeline_default_stage_id: null,
                          });
                        }}
                      />,
                    )}
                  </Form.Item>
                  <Form.Item>
                    {props.form.getFieldDecorator('hubspot_pipeline_default_stage_id', {
                      initialValue: props.projects.projectDetail.hubspot_pipeline_default_stage_id || null,
                    })(
                      <SelectInput
                        disabled={!createHubspotDeal}
                        showArrow
                        placeholder={<FormattedMessage
                          id="Component.AccountSettings.New.HubspotSection.DefaultDealStagePlaceholder"
                        />}
                        options={dealStages}
                      />,
                    )}
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <h3><FormattedMessage id="Component.AccountSettings.New.HubspotSection.SelectUsersTitle" /></h3>
                <p><FormattedMessage id="Component.AccountSettings.New.HubspotSection.SelectUsersDescription" /></p>
                <TwoColumnsSelect
                  disabled={!hubspotPortalId}
                  optionsFieldName="hubspot_users"
                  selectedFieldName="hubspot_users_selected"
                  form={props.form}
                  optionValues={props.hubspotOwners.all.map((owner) => {
                    return { key: owner.hubspot_user_id, title: `${owner.first_name} ${owner.last_name}` };
                  })}
                  selectedValues={Array.isArray(props.hubspotOwners.assigned[projectId]) ? props.hubspotOwners.assigned[projectId].map(owner => owner.hubspot_user_id) : []}
                />
              </Row>
              <Row style={{ marginTop: '32px' }}>
                <h3><FormattedMessage id="Component.AccountSettings.New.HubspotSection.SelectLxpUsersTitle" /></h3>
                <p><FormattedMessage id="Component.AccountSettings.New.HubspotSection.SelectLxpUsersDescription" /></p>
                <TwoColumnsSelect
                  disabled={!hubspotPortalId}
                  optionsFieldName="lxp_hubspot_users"
                  selectedFieldName="lxp_hubspot_users_selected"
                  form={props.form}
                  optionValues={users.map((user) => {
                    return { key: user.id, title: `${user.first_name} ${user.last_name}` };
                  })}
                  selectedValues={hubspotPermissions.map(item => item.user_id)}
                />
              </Row>
            </Card>
          </Collapse.Panel>}

          {editMode && <Collapse.Panel
            key="installCodes" header={<h3><FormattedMessage
              id="Component.AccountSettings.New.InstallCodes"
            /></h3>}
          >
            <Card className={[styles.noMarginTop, styles.noMarginBottom, styles.noBorderTop].join(' ')}>
              <Input.TextArea rows={10} defaultValue={installCode} readOnly></Input.TextArea>
              <div className={[styles.btnNoBorder, styles.btnCopy].join(' ')}>
                <Icon type={isCopiedInstallCode ? 'check' : 'copy'} onClick={() => copyInstallCode()} />
              </div>
            </Card>
          </Collapse.Panel>}

          {/* AI Assistent*/}
          {llmEnabled && editMode && <Collapse.Panel
            key="llm"
            header={<h3><FormattedMessage id="Component.AccountSettings.Edit.LanguageModelSection.LLMPromptTitle" />
            </h3>}
          >
            <Spin spinning={llmPromptInProgress}>
              <Card className={[styles.noMarginTop, styles.noMarginBottom, styles.noBorderTop].join(' ')}>
                <FormattedMessage id="Component.AccountSettings.Edit.LanguageModelSection.LLMPromptDescription1" /><br />
                <FormattedMessage id="Component.AccountSettings.Edit.LanguageModelSection.LLMPromptDescription2" /><br />
                <FormattedMessage id="Component.AccountSettings.Edit.LanguageModelSection.LLMPromptDescription3" /><br />
                <Row type={'flex'} gutter={32} style={{ marginTop: '30px' }}>
                  <Col span={24}>
                    <Form.Item
                      label={<FormattedMessage
                        id="Component.AccountSettings.Edit.LanguageModelSection.CustomizeYourPromptLabel"
                      />}
                    >
                      {props.form.getFieldDecorator('llm_prompt', {
                        initialValue: props.projects.projectDetail.llm_prompt,
                        rules: [{
                          required: false,
                        }],
                      })(<
                        TextArea
                        style={{ height: '200px' }}
                        autoSize={{ minRows: 6, maxRows: 10 }}
                      />)}
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label={<FormattedMessage id="Component.AccountSettings.Edit.LanguageModelSection.CompanyLabel" />}
                    >
                      {props.form.getFieldDecorator('llm_test_company_id', {})(
                        <RemoteSelectInput
                          nameKey="company_name"
                          remoteLoader={companiesLoader(projectId)}
                          notFoundContent={props.intl.formatMessage({ id: 'Component.AccountSettings.Edit.LanguageModelSection.TestCompanyNoCompaniesFound' })}
                          placeholder={props.intl.formatMessage({ id: 'Common.RemoteSelect.Placeholder' })}
                          showArrow
                          onChange={(value) => {
                            setLlmPromptTestCompanyId(value);
                          }}
                        />,
                      )}
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Button
                      disabled={!llmPromptTestCompanyId}
                      type="primary"
                      style={{ marginTop: '48px' }}
                      onClick={() => testLlmPrompt(
                        props.form.getFieldValue('llm_test_company_id'),
                        props.form.getFieldValue('llm_prompt'),
                      )}
                    >
                      <FormattedMessage id="Common.Test" />
                    </Button>
                  </Col>
                </Row>
                {llmPromptResult &&
                  <Row>
                    <Comment
                      author="AI Sales Assistant"
                      avatar={<Avatar src="/dist/ai-sales-avatar.jpg" />}
                      content={
                        <p style={{ whiteSpace: 'pre-line' }}>
                          {llmPromptResult}
                        </p>
                      }
                    />
                  </Row>}
              </Card>
            </Spin>
          </Collapse.Panel>}
        </Collapse>
        <ButtonBar cancelAction={cancelAction} />
      </Form>
    </div>
  </Spin>);
};

ProjectItem.defaultProps = {
  hubspotOwners: {
    all: [],
    assigned: [],
  },
  hubspot: {
    pipelines: [],
  },
};

export default connect(({
  app, loading, projects, users, hubspotOwners, hubspot,
}) => ({
  app, loading, projects, users, hubspotOwners, hubspot,
}))(Form.create()(injectIntl(ProjectItem)));



// WEBPACK FOOTER //
// ./src/components/Projects/ProjectItem/ProjectItem.js